import React from "react";
import {
  Box,
  Button,
  ButtonGroup,
  Stack,
  Tab,
  Tabs,
  Tooltip,
  Typography,
} from "@mui/material";
import {
  AccountTree as AutoFlowIcon,
  BarChart as KpisIcon,
  History as AuditIcon,
  Extension as IntegrateIcon,
  Visibility as OverviewIcon,
} from "@mui/icons-material";
import { Link as RouterLink, useHistory } from "react-router-dom";
import PropTypes from "prop-types";

import { usePermission } from "src/helpers/Auth";
import { generateLink } from "src/routes";
import { useAutomationConfigTemplate } from "../services/AutomationListServices";

export const TEMPLATE_TAB_DEFS = [
  {
    value: "overview",
    label: "Overview",
    icon: <OverviewIcon fontSize="small" />,
    routeName: "Automation Template",
    perm: "autotemplate:read",
    color: "#2395b7", // teal-blue
  },
  {
    value: "autoflow",
    label: "AutoFlow Config",
    icon: <AutoFlowIcon fontSize="small" />,
    routeName: "AutoFlow",
    perm: "autoconfig:read",
    color: "#19857b", // teal-green
  },
  {
    value: "kpis",
    label: "Performance Metrics",
    icon: <KpisIcon fontSize="small" />,
    routeName: "Automation KPIs",
    perm: "autodata:read",
    color: "#2e7d32", // green
  },
  {
    value: "audit",
    label: "Audit History",
    icon: <AuditIcon fontSize="small" />,
    routeName: "Automation Records",
    perm: "autodata:read",
    color: "#ed6c02", // orange
  },
  {
    value: "integrate",
    label: "Integrate",
    icon: <IntegrateIcon fontSize="small" />,
    routeName: "Integrate Automation",
    perm: "autoconfig:read",
    color: "#6a1b9a", // purple
  },
];

// Resolves which TEMPLATE_TAB_DEFS entries the current user may see,
// optionally hiding tabs by value.
const useVisibleTabs = (exclude = []) => {
  const permMap = {
    "autotemplate:read": usePermission("autotemplate:read").canIt,
    "autoconfig:read": usePermission("autoconfig:read").canIt,
    "autodata:read": usePermission("autodata:read").canIt,
  };
  return TEMPLATE_TAB_DEFS.filter(
    (t) => permMap[t.perm] && !exclude.includes(t.value),
  );
};

export const TemplateTabs = ({ templateId, activeValue }) => {
  const history = useHistory();
  const visibleTabs = useVisibleTabs();
  const activeTab = TEMPLATE_TAB_DEFS.find((t) => t.value === activeValue);
  const indicatorColor = activeTab?.color ?? "#2395b7";

  return (
    <Box sx={{ borderBottom: 1, borderColor: "divider", mb: 3 }}>
      <Tabs
        value={activeValue}
        variant="scrollable"
        scrollButtons="auto"
        textColor="inherit"
        data-testid="cap-tmpl-tabs"
        TabIndicatorProps={{
          sx: { backgroundColor: indicatorColor, height: 3 },
        }}
        onChange={(_, value) => {
          const tab = TEMPLATE_TAB_DEFS.find((t) => t.value === value);
          if (!tab || value === activeValue) return;
          history.push(
            generateLink(tab.routeName, {
              automation_config_template_id: templateId,
            }),
          );
        }}
      >
        {visibleTabs.map((t) => (
          <Tab
            key={t.value}
            value={t.value}
            label={t.label}
            icon={t.icon}
            iconPosition="start"
            data-testid={`cap-tmpl-tab-${t.value}`}
            sx={{
              textTransform: "none",
              minHeight: 48,
              color: `${t.color} !important`,
              opacity: 0.65,
              "& .MuiSvgIcon-root, & svg": {
                color: `${t.color} !important`,
              },
              "&.Mui-selected": {
                color: `${t.color} !important`,
                opacity: 1,
                fontWeight: 600,
                bgcolor: `${t.color}1F`,
              },
              "&:hover": {
                opacity: 1,
                bgcolor: `${t.color}14`,
              },
            }}
          />
        ))}
      </Tabs>
    </Box>
  );
};

TemplateTabs.propTypes = {
  templateId: PropTypes.oneOfType([PropTypes.number, PropTypes.string])
    .isRequired,
  activeValue: PropTypes.string.isRequired,
};

// Compact horizontal nav — same routes as TemplateTabs, intended for
// embedding inside other UI (e.g. flow nodes) where a full tab bar is
// too large. `exclude` hides individual tabs by value; `size` picks
// "small" / "medium" / "large" MUI button sizes.
export const TemplateNavButtons = ({
  templateId,
  exclude,
  size,
  testIdPrefix,
}) => {
  const history = useHistory();
  const visibleTabs = useVisibleTabs(exclude);

  return (
    <ButtonGroup
      size={size}
      variant="outlined"
      data-testid={`${testIdPrefix}-toolbar`}
    >
      {visibleTabs.map((t) => (
        <Tooltip key={t.value} title={t.label} placement="top" arrow>
          <Button
            onClick={() =>
              history.push(
                generateLink(t.routeName, {
                  automation_config_template_id: templateId,
                }),
              )
            }
            data-testid={`${testIdPrefix}-btn-${t.value}-${templateId}`}
            startIcon={t.icon}
            sx={{ textTransform: "none" }}
          >
            {t.label}
          </Button>
        </Tooltip>
      ))}
    </ButtonGroup>
  );
};

TemplateNavButtons.propTypes = {
  templateId: PropTypes.oneOfType([PropTypes.number, PropTypes.string])
    .isRequired,
  exclude: PropTypes.arrayOf(PropTypes.string),
  size: PropTypes.oneOf(["small", "medium", "large"]),
  testIdPrefix: PropTypes.string,
};

TemplateNavButtons.defaultProps = {
  exclude: [],
  size: "small",
  testIdPrefix: "cap-tmpl",
};

export const TemplatePageHeader = ({
  templateId,
  pageLabel,
  subtitle,
  testIdPrefix,
}) => {
  const { data: template } = useAutomationConfigTemplate(templateId);

  return (
    <Box sx={{ mb: 3 }} data-testid={`cap-tmpl-header-${templateId}`}>
      <Stack direction="row" alignItems="center" spacing={2}>
        <Box
          component="img"
          src="/images/product-logos/cap.png"
          alt="CAP AI"
          sx={{ height: 44, width: 44, objectFit: "contain" }}
        />
        <Box sx={{ flex: 1 }}>
          <Typography
            variant="h4"
            sx={{ fontWeight: 600 }}
            data-testid={`${testIdPrefix}-heading`}
          >
            {pageLabel
              ? `${pageLabel} — ${template?.name ?? ""}`.trim()
              : (template?.name ?? "Automation Template")}
          </Typography>
          {subtitle && (
            <Typography variant="body2" color="text.secondary">
              {subtitle}
            </Typography>
          )}
        </Box>
        {pageLabel === "Overview" &&
          template?.parent_automation_config_template_fk && (
            <Button
              variant="outlined"
              size="small"
              component={RouterLink}
              to={generateLink("Automation Template", {
                automation_config_template_id:
                  template.parent_automation_config_template_fk,
              })}
              sx={{ textTransform: "none" }}
              data-testid={`cap-tmpl-btn-parent-${templateId}`}
            >
              View parent template
            </Button>
          )}
      </Stack>
    </Box>
  );
};

TemplatePageHeader.propTypes = {
  templateId: PropTypes.oneOfType([PropTypes.number, PropTypes.string])
    .isRequired,
  pageLabel: PropTypes.string,
  subtitle: PropTypes.string,
  testIdPrefix: PropTypes.string,
};

TemplatePageHeader.defaultProps = {
  testIdPrefix: "cap-tmpl",
};
